/** Local, deterministic street planning. Painting and lifecycle flags belong to the caller. */
import { buildingType } from '../data/buildings.ts';
import { alliedWith, OB_SOLID, Terrain, WallKind } from './types.ts';
import type { World } from './world.ts';

export const MAX_AUTO_ROAD_LENGTH = 48;

function dryAccessTerrain(t: number): boolean {
  return t === Terrain.AshPlain || t === Terrain.Ruin || t === Terrain.Road
    || t === Terrain.Forest || t === Terrain.StoneOutcrop || t === Terrain.IronSeam;
}

/** Existing roads are retained; only these dry surfaces may receive a new road. */
export function isAutoRoadGround(w: World, tile: number): boolean {
  const m = w.map;
  return tile >= 0 && tile < m.terrain.length && m.building[tile] < 0
    && m.wall[tile] === WallKind.None && !m.rubble[tile] && !m.resourceSolid?.[tile]
    && !(w.obstacleGrid?.[tile] && (w.obstacleGrid[tile] & OB_SOLID)) && w.nodes.at(tile) < 0
    // Resource terrain is a ground texture; nodes hold the actual reserves.
    // Empty gaps between trees/outcrops can carry a street without harvesting.
    && dryAccessTerrain(m.terrain[tile]);
}

function perimeter(w: World, building: number): number[] {
  const b = w.buildings, m = w.map, def = buildingType(b.typeId[building]);
  const x = b.tx[building], y = b.ty[building], mid = x + Math.floor(def.w / 2);
  const out: number[] = [];
  const add = (tx: number, ty: number) => { if (m.inBounds(tx, ty)) out.push(m.idx(tx, ty)); };
  // The visible front faces down the map. Prefer the door, then its frontage,
  // the sides, and finally the back when another structure blocks the front.
  add(mid, y + def.h);
  for (let d = 1; d < def.w; d++) {
    if (mid - d >= x) add(mid - d, y + def.h);
    if (mid + d < x + def.w) add(mid + d, y + def.h);
  }
  for (let dy = def.h - 1; dy >= 0; dy--) { add(x + def.w, y + dy); add(x - 1, y + dy); }
  for (let dx = 0; dx < def.w; dx++) add(x + dx, y - 1);
  return out;
}

/** Cheap lifecycle guard: established frontage does not need another street. */
export function hasBuildingRoadAccess(w: World, building: number): boolean {
  if (!w.buildings.isAlive(building)) return false;
  const m = w.map, canGate = alliedWith(w.buildings.team[building], w.wallTeam);
  return perimeter(w, building).some(k => {
    if (m.building[k] >= 0 || m.rubble[k] || m.resourceSolid?.[k] || w.nodes.at(k) >= 0 || ((w.obstacleGrid?.[k] ?? 0) & OB_SOLID)) return false;
    if (m.wall[k] !== WallKind.None && !(canGate && m.wall[k] === WallKind.Gate)) return false;
    return m.terrain[k] === Terrain.Road || m.terrain[k] === Terrain.Bridge
      || (canGate && m.wall[k] === WallKind.Gate && dryAccessTerrain(m.terrain[k]));
  });
}

interface Step { tile: number; cost: number; steps: number; previous: Step | null }

/** Integer costs and tile-index ties keep every client and replay on the same route. */
class Frontier {
  private readonly heap: Step[] = [];
  private before(a: Step, b: Step): boolean { return a.cost < b.cost || (a.cost === b.cost && (a.steps < b.steps || (a.steps === b.steps && a.tile < b.tile))); }
  push(s: Step): void {
    const h = this.heap; let i = h.length; h.push(s);
    while (i > 0) { const p = (i - 1) >> 1; if (!this.before(s, h[p])) break; h[i] = h[p]; i = p; }
    h[i] = s;
  }
  pop(): Step | undefined {
    const h = this.heap, first = h[0], last = h.pop();
    if (!h.length || !last) return first;
    let i = 0;
    while (i * 2 + 1 < h.length) {
      let c = i * 2 + 1; if (c + 1 < h.length && this.before(h[c + 1], h[c])) c++;
      if (!this.before(h[c], last)) break;
      h[i] = h[c]; i = c;
    }
    h[i] = last; return first;
  }
}

/**
 * Entrance-to-street tile indices, including existing road/bridge/gate transit.
 * No changes to the map, resource nodes, RNG or caches. An empty result means
 * no safe local connection exists; automatic streets never clear an obstacle
 * or substitute a ford for a bridge. The limit counts cardinal moves.
 */
export function planBuildingAccess(w: World, building: number, maxLength = MAX_AUTO_ROAD_LENGTH): number[] {
  const b = w.buildings, m = w.map;
  if (!b.isAlive(building) || !Number.isFinite(maxLength) || maxLength < 1) return [];
  maxLength = Math.min(MAX_AUTO_ROAD_LENGTH, Math.floor(maxLength));
  const team = b.team[building], canGate = alliedWith(team, w.wallTeam);
  const neighbors = (k: number): number[] => {
    const x = k % m.w, y = Math.floor(k / m.w), out: number[] = [];
    if (y + 1 < m.h) out.push(k + m.w);
    if (x > 0) out.push(k - 1);
    if (x + 1 < m.w) out.push(k + 1);
    if (y > 0) out.push(k - m.w);
    return out;
  };
  const clear = (k: number) => m.building[k] < 0 && !m.rubble[k] && !m.resourceSolid?.[k]
    && !(w.obstacleGrid?.[k] && (w.obstacleGrid[k] & OB_SOLID)) && w.nodes.at(k) < 0
    && (m.wall[k] === WallKind.None || (canGate && m.wall[k] === WallKind.Gate));
  const dry = (k: number) => dryAccessTerrain(m.terrain[k]);
  const bridgeValid = new Map<number, boolean>();
  const completeBridge = (start: number): boolean => {
    const known = bridgeValid.get(start); if (known !== undefined) return known;
    const queue = [start], seen = new Set(queue); let minX = m.w, maxX = 0, minY = m.h, maxY = 0;
    for (let i = 0; i < queue.length && queue.length <= 512; i++) {
      const k = queue[i], x = k % m.w, y = Math.floor(k / m.w);
      minX = Math.min(minX, x); maxX = Math.max(maxX, x); minY = Math.min(minY, y); maxY = Math.max(maxY, y);
      for (const n of neighbors(k)) if (!seen.has(n) && m.terrain[n] === Terrain.Bridge && clear(n)) { seen.add(n); queue.push(n); }
    }
    let left = false, right = false, top = false, bottom = false;
    for (const k of queue) {
      const x = k % m.w, y = Math.floor(k / m.w);
      if (x === minX && x > 0 && dry(k - 1) && clear(k - 1)) left = true;
      if (x === maxX && x + 1 < m.w && dry(k + 1) && clear(k + 1)) right = true;
      if (y === minY && y > 0 && dry(k - m.w) && clear(k - m.w)) top = true;
      if (y === maxY && y + 1 < m.h && dry(k + m.w) && clear(k + m.w)) bottom = true;
    }
    const valid = queue.length <= 512 && ((left && right) || (top && bottom));
    for (const k of queue) bridgeValid.set(k, valid);
    return valid;
  };
  const passable = (k: number) => clear(k) && (dry(k) || (m.terrain[k] === Terrain.Bridge && completeBridge(k)));
  const street = (k: number) => passable(k) && (m.terrain[k] === Terrain.Road || m.terrain[k] === Terrain.Bridge || m.wall[k] === WallKind.Gate);
  const entrances = perimeter(w, building).filter(passable);
  if (!entrances.length) return [];

  // A street around the allied Hearth is a better destination than a stranded
  // one-tile track. Flood only existing streets, with a fixed ceiling for maps
  // carrying very large legacy road systems.
  const hearth = w.hearth;
  const hearthEdge = new Set(hearth !== building && b.isAlive(hearth) && alliedWith(team, b.team[hearth]) ? perimeter(w, hearth).filter(passable) : []);
  // Remote outposts with no street in range should not flood thousands of
  // otherwise empty tiles on every scheduled retry. This is a conservative
  // distance test only: the actual search still decides reachability.
  const entranceXY = entrances.map(k => [k % m.w, Math.floor(k / m.w)] as const);
  const inRange = (x: number, y: number) => entranceXY.some(([ex, ey]) => Math.abs(x - ex) + Math.abs(y - ey) <= maxLength);
  let localDestination = [...hearthEdge].some(k => inRange(k % m.w, Math.floor(k / m.w)));
  const x0 = Math.max(0, Math.min(...entranceXY.map(p => p[0])) - maxLength);
  const x1 = Math.min(m.w - 1, Math.max(...entranceXY.map(p => p[0])) + maxLength);
  const y0 = Math.max(0, Math.min(...entranceXY.map(p => p[1])) - maxLength);
  const y1 = Math.min(m.h - 1, Math.max(...entranceXY.map(p => p[1])) + maxLength);
  for (let y = y0; y <= y1 && !localDestination; y++) for (let x = x0; x <= x1; x++) {
    const k = m.idx(x, y);
    if ((m.terrain[k] === Terrain.Road || m.terrain[k] === Terrain.Bridge || (canGate && m.wall[k] === WallKind.Gate))
      && inRange(x, y) && passable(k)) { localDestination = true; break; }
  }
  if (!localDestination) return [];
  const connected = new Set<number>(), queue: number[] = [];
  for (const k of hearthEdge) if (street(k)) { connected.add(k); queue.push(k); }
  for (let i = 0; i < queue.length && queue.length < 8192; i++) for (const n of neighbors(queue[i])) {
    if (!connected.has(n) && street(n)) { connected.add(n); queue.push(n); }
  }
  const penalties = new Map<number, number>();
  const destinationPenalty = (k: number): number => {
    if (hearthEdge.has(k) || connected.has(k)) return 0;
    // A gate can carry a route but is not itself a destination unless an
    // established street already connects it to the Hearth.
    if (!street(k) || (m.terrain[k] !== Terrain.Road && m.terrain[k] !== Terrain.Bridge)) return Infinity;
    const known = penalties.get(k); if (known !== undefined) return known;
    const seen = new Set([k]), fragment = [k];
    for (let i = 0; i < fragment.length && fragment.length < 6; i++) for (const n of neighbors(fragment[i])) {
      if (!seen.has(n) && street(n)) { seen.add(n); fragment.push(n); }
    }
    const penalty = fragment.length >= 6 ? 60 : 100;
    for (const n of fragment) penalties.set(n, penalty);
    return penalty;
  };

  const search = (starts: number[]): number[] => {
    const frontier = new Frontier(), costs = new Map<number, number>();
    for (let i = 0; i < starts.length; i++) {
      // A small fallback preference keeps other front tiles ahead of the back
      // when both provide equally useful access.
      const k = starts[i]; costs.set(k, i); frontier.push({ tile: k, cost: i, steps: 0, previous: null });
    }
    let best: Step | null = null, bestScore = Infinity, step: Step | undefined;
    while ((step = frontier.pop())) {
      const { tile, cost, steps } = step;
      if (cost !== costs.get(tile)) continue;
      if (cost > bestScore) break;
      const score = cost + destinationPenalty(tile);
      if (score < bestScore || (score === bestScore && best && tile < best.tile)) { best = step; bestScore = score; }
      if (steps >= maxLength) continue;
      for (const n of neighbors(tile)) {
        if (!passable(n)) continue;
        const next = cost + (m.terrain[n] === Terrain.Road || m.terrain[n] === Terrain.Bridge ? 4 : 10);
        if (next >= (costs.get(n) ?? Infinity) || next > bestScore) continue;
        costs.set(n, next); frontier.push({ tile: n, cost: next, steps: steps + 1, previous: step });
      }
    }
    if (!best) return [];
    const result: number[] = [];
    for (let cursor: Step | null = best; cursor; cursor = cursor.previous) result.push(cursor.tile);
    return result.reverse();
  };
  const preferred = search(entrances.slice(0, 1));
  return preferred.length ? preferred : search(entrances.slice(1));
}
